/**
 * NJM reusable Logo component — v4 (2026-10, Dashboard + Shell Fidelity Pass: restore the
 * exact "yellow disc + black film-strip glyph" mark from the NJM OS Design System 2.0 v3
 * source bundle — Phase 1 had regressed this to an over-simplified single black pill shape).
 *
 * ONE SVG mark, used exactly as provided (never rotated, skewed, distorted, or recoloured):
 * an NJM Yellow (#FFC817) disc with an Ink (#111111) stacked film-strip glyph (cut with
 * yellow "perforations") on top — same geometry across NJM OS, Recce, and every portal, in
 * both light and dark contexts (the yellow disc reads clearly on both Page/Surface and
 * Charcoal). The "NJM PRODUCTIONS" wordmark is rendered as real text (not baked into the
 * mark) so it's always crisp at any size — this is the ONE component every login screen, the
 * sidebar, and the dark loading screen render through, so changing the mark here updates it
 * everywhere.
 *
 * Usage:
 *   <Logo />                                    mark + "NJM PRODUCTIONS" text, on a light bg
 *   <Logo theme="dark" />                       mark + text, on a dark bg
 *   <Logo iconOnly />                            mark only (collapsed sidebar, favicon-like)
 *   <Logo responsive />                          mark + text ≥sm, mark-only below sm (mobile)
 *   <Logo iconOnly showBuildLabel />              mark + "NJM BUILD" lockup (all-caps,
 *                                                 Montserrat bold — black on light bg, white on dark bg)
 *
 * IMPORTANT: the mark must always render upright — never rotated, skewed, or otherwise distorted.
 */
function Mark({ className = "", decorative = false }) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={`fill-brand ${className}`}
      aria-hidden={decorative ? "true" : undefined}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : "NJM"}
    >
      <circle cx="32" cy="32" r="32" />
      <g className="fill-ink">
        <path d="M21.7 18L44.7 19.8V25.1H21.7Z" />
        <path d="M18 26H41V31.1L18 28.8Z" />
        <path d="M23 30.2L46 32.5V39.2L23 32.8Z" />
        <path d="M21.4 33.6L44.4 40.2V44.8H21.4Z" />
      </g>
      <g className="fill-brand">
        <rect x="22.9" y="17.5" width="2.2" height="2.8" />
        <rect x="22.9" y="23.4" width="2.2" height="2" />
        <rect x="41.3" y="19.2" width="2.1" height="1.3" />
        <rect x="41.3" y="23.5" width="2.1" height="2" />
        <rect x="19.2" y="25.5" width="2.2" height="1.7" />
        <rect x="37.6" y="25.5" width="2.2" height="1.8" />
        <rect x="37.6" y="30.2" width="2.2" height="1.2" />
        <rect x="42.5" y="32" width="2.1" height="2.5" />
        <rect x="42.5" y="37.5" width="2.1" height="2" />
        <rect x="22.6" y="37.9" width="2.2" height="2.9" />
        <rect x="22.6" y="43.6" width="2.2" height="1.5" />
        <rect x="41" y="43.7" width="2.2" height="1.5" />
        <rect x="41" y="39.4" width="2.2" height="1.5" />
      </g>
    </svg>
  );
}

const HEIGHT_CLASSES = {
  sm: "h-7",
  md: "h-7 sm:h-8",
  lg: "h-9 sm:h-11",
  xl: "h-12 sm:h-14",
};

const WORDMARK_TEXT_CLASSES = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base sm:text-lg",
  xl: "text-xl sm:text-2xl",
};

const LABEL_TEXT_CLASSES = WORDMARK_TEXT_CLASSES;

export function Logo({
  theme = "light",
  size = "md",
  iconOnly = false,
  responsive = false,
  showBuildLabel = false,
  buildLabelText = "NJM Build",
  className = "",
  testId = "njm-logo",
}) {
  const heightCls = HEIGHT_CLASSES[size] || HEIGHT_CLASSES.md;
  const wordmarkCls = WORDMARK_TEXT_CLASSES[size] || WORDMARK_TEXT_CLASSES.md;
  const textColor = theme === "dark" ? "text-white" : "text-ink";

  const wordmark = (
    <span className={`font-editorial uppercase font-bold tracking-tight leading-none ${wordmarkCls} ${textColor}`}>
      NJM <span className="opacity-70">Productions</span>
    </span>
  );

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} data-testid={testId}>
      <Mark className={`${heightCls} w-auto shrink-0`} decorative={!iconOnly} />
      {!iconOnly && (
        responsive ? <span className="hidden sm:inline">{wordmark}</span> : wordmark
      )}
      {showBuildLabel && (
        <span
          data-testid="njm-logo-build-label"
          className={`font-editorial uppercase ${LABEL_TEXT_CLASSES[size] || LABEL_TEXT_CLASSES.md} font-bold tracking-tight border-l pl-2.5 leading-none ${
            theme === "dark" ? "text-white border-white/25" : "text-ink border-ink/15"
          }`}
        >
          {buildLabelText}
        </span>
      )}
    </span>
  );
}
